import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  HeartIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ViewColumnsIcon,
  Bars3Icon,
  ChevronDownIcon,
  FunnelIcon,
  ShareIcon,
  MapPinIcon,
  ClockIcon,
  EyeIcon,
  XMarkIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckIcon,
  HomeIcon,
  TruckIcon,
  DevicePhoneMobileIcon,
  WrenchScrewdriverIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { adsApi, categoriesApi, AdListItem, SearchRequest, CategoryAttribute, Category } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import DealdenLoader from '../components/DealdenLoader';
import CitiesModal from '../components/CitiesModal';
import '../styles/promotional-effects.css';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('date_new');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;
  
  // Category navigation state
  const [selectedCategoryPath, setSelectedCategoryPath] = useState<Category[]>([]);
  
  // Cities modal state
  const [isCitiesModalOpen, setIsCitiesModalOpen] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  
  // Search filters state
  const [filters, setFilters] = useState({
    searchTerm: searchParams.get('search') || '',
    categoryId: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    city: searchParams.get('city') || '',
    province: searchParams.get('province') || '',
    bodyType: [] as string[],
    fuelType: '',
    transmission: '',
    yearFrom: '',
    yearTo: '',
    mileageFrom: '',
    mileageTo: '',
    color: '',
    condition: '',
    attributes: {} as { [key: string]: string | string[] }
  });



  const [expandedSections, setExpandedSections] = useState({
    location: true,
    category: true,
    price: true,
    bodyType: true,
    year: true,
    attributes: true
  });

  // Fetch categories for filter dropdown
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll()
  });

  // Helper function to find category by slug or ID
  const findCategoryBySlugOrId = (slugOrId: string, categoriesData: any[]) => {
    if (!categoriesData || !slugOrId) return null;
    
    // First try to find by slug
    const bySlug = categoriesData.find(cat => cat.slug === slugOrId);
    if (bySlug) return bySlug;
    
    // Fallback to find by ID (for backward compatibility)
    return categoriesData.find(cat => cat.id === slugOrId);
  };

  // Get the selected category (by slug or ID) - only when categories are loaded
  const selectedCategory = React.useMemo(() => {
    return categories?.data ? findCategoryBySlugOrId(filters.categoryId, categories.data) : null;
  }, [categories?.data, filters.categoryId]);



  // Get the currently selected category ID (convert slug to ID if needed)
  const currentCategoryId = selectedCategory?.id || (selectedCategoryPath.length > 0 ? selectedCategoryPath[selectedCategoryPath.length - 1].id : '');
  
  // Fetch category attributes when any category is selected
  const { data: categoryAttributes } = useQuery({
    queryKey: ['category-attributes', currentCategoryId],
    queryFn: () => categoriesApi.getAttributes(currentCategoryId),
    enabled: !!currentCategoryId
  });

  // Fetch ads based on current filters
  const { data: adsResponse, isLoading, refetch } = useQuery({
    queryKey: ['ads', filters, sortBy, currentPage],
    queryFn: () => {
      const searchRequest: SearchRequest = {
        searchTerm: filters.searchTerm || undefined,
        categoryId: selectedCategory?.id || undefined, // Use the actual category ID for API call
        minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
        maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
        city: filters.city || undefined,
        province: filters.province || undefined,
        sortBy: sortBy as any,
        page: currentPage,
        pageSize: pageSize
      };
      return adsApi.search(searchRequest);
    }
  });

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
    
    const newParams = new URLSearchParams(searchParams);
    if (value && (typeof value === 'string' ? value : value.length > 0)) {
      if (typeof value === 'string') {
        newParams.set(key === 'searchTerm' ? 'search' : key, value);
      } else {
        newParams.set(key, value.join(','));
      }
    } else {
      newParams.delete(key === 'searchTerm' ? 'search' : key);
    }
    setSearchParams(newParams);
  };

  const handleAttributeChange = (attributeName: string, value: string | string[]) => {
    setFilters(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [attributeName]: value
      }
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      categoryId: '',
      minPrice: '',
      maxPrice: '',
      city: '',
      province: '',
      bodyType: [],
      fuelType: '',
      transmission: '',
      yearFrom: '',
      yearTo: '',
      mileageFrom: '',
      mileageTo: '',
      color: '',
      condition: '',
      attributes: {}
    });
    setSearchParams({});
    setCurrentPage(1);
    setSelectedCategoryPath([]);
    setSelectedCities([]);
  };

  // Cities modal handlers
  const openCitiesModal = () => {
    setIsCitiesModalOpen(true);
  };

  const closeCitiesModal = () => {
    setIsCitiesModalOpen(false);
  };

  const handleCitiesSelect = (cities: string[]) => {
    setSelectedCities(cities);
    handleFilterChange('city', cities.join(','));
  };

  // Helper functions for category navigation
  const getCurrentLevelCategories = () => {
    if (!categories?.data) return [];
    
    if (selectedCategoryPath.length === 0) {
      // Show root categories (no parent)
      return categories.data.filter(cat => !cat.parentCategoryId);
    } else {
      // Show children of the last selected category
      const currentParent = selectedCategoryPath[selectedCategoryPath.length - 1];
      return getChildCategories(currentParent.id);
    }
  };

  const getChildCategories = (parentId: string) => {
    if (!categories?.data) return [];
    
    // Always check flat structure using parentCategoryId first (this matches your database)
    const flatChildren = categories.data.filter(cat => cat.parentCategoryId === parentId);
    
    if (flatChildren.length > 0) {
      return flatChildren;
    }
    
    // Fallback to nested subCategories if available
    const parent = categories.data.find(cat => cat.id === parentId);
    if (parent?.subCategories && parent.subCategories.length > 0) {
      return parent.subCategories;
    }
    
    return [];
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.categoryId) count++;
    if (filters.minPrice || filters.maxPrice) count++;
    if (filters.city) count++;
    if (filters.province) count++;
    if (filters.bodyType.length > 0) count++;
    if (filters.fuelType) count++;
    if (filters.transmission) count++;
    if (filters.yearFrom || filters.yearTo) count++;
    if (filters.mileageFrom || filters.mileageTo) count++;
    if (filters.color) count++;
    if (filters.condition) count++;
    Object.values(filters.attributes).forEach(val => {
      if (val && (typeof val === 'string' ? val : val.length > 0)) count++;
    });
    return count;
  };

  const ads = adsResponse?.data.ads || [];
  const totalCount = adsResponse?.data.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);



  // Get root categories (categories without parent)
  const rootCategories = categories?.data?.filter(cat => !cat.parentCategoryId) || [];
  
  // Get subcategories for selected category
  const subcategories = categories?.data?.filter(cat => cat.parentCategoryId === selectedCategory?.id) || [];

  // Render filter component based on attribute type
  const renderAttributeFilter = (attribute: CategoryAttribute) => {
    if (!attribute.isFilterable) return null;

    const value = filters.attributes[attribute.name] || '';

    switch (attribute.type) {
      case 'Dropdown':
        const options = attribute.options?.split('\n').filter(opt => opt.trim()) || [];
        return (
          <div className="relative">
            <select
              value={value as string}
              onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                             className="w-full appearance-none px-3 py-2 pl-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
              style={{ backgroundImage: 'none' }}
            >
              <option value="">Any {attribute.label}</option>
              {options.map((option, index) => (
                <option key={index} value={option.trim()}>
                  {option.trim()}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        );

      case 'Number':
        return (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder="From"
              value={(value as string).split('-')[0] || ''}
              onChange={(e) => {
                const toValue = (value as string).split('-')[1] || '';
                handleAttributeChange(attribute.name, e.target.value + (toValue ? `-${toValue}` : ''));
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <input
              type="number"
              placeholder="To"
              value={(value as string).split('-')[1] || ''}
              onChange={(e) => {
                const fromValue = (value as string).split('-')[0] || '';
                handleAttributeChange(attribute.name, (fromValue ? `${fromValue}-` : '') + e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        );

      case 'Boolean':
        return (
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name={attribute.name}
                value="true"
                checked={value === 'true'}
                onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                className="mr-2 text-primary-600"
              />
              <span className="text-sm">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={attribute.name}
                value="false"
                checked={value === 'false'}
                onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                className="mr-2 text-primary-600"
              />
              <span className="text-sm">No</span>
            </label>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
            placeholder={`Enter ${attribute.label.toLowerCase()}`}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700">
                  Home
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">Search Ads</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                {getActiveFiltersCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Clear all ({getActiveFiltersCount()})
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search in ads..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Location - Divar Style */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Location</h3>
                <div className="space-y-3">
                  <div 
                    onClick={openCitiesModal}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg cursor-pointer text-sm bg-white hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span className={selectedCities.length > 0 ? "text-gray-900" : "text-gray-500"}>
                      {selectedCities.length > 0 ? `${selectedCities.length} cities selected` : "Select Location"}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Cities Display */}
                  {selectedCities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCities.map(city => (
                        <span 
                          key={city} 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800"
                        >
                          {city}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              const newCities = selectedCities.filter(c => c !== city);
                              setSelectedCities(newCities);
                              handleFilterChange('city', newCities.join(','));
                            }}
                            className="ml-1 hover:text-red-600"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Category - Hierarchical Navigation */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Categories</h3>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {/* Breadcrumb Navigation */}
                  {(selectedCategoryPath.length > 0) && (
                    <div className="flex items-center p-2 mb-2 border-b border-gray-100">
                      <button
                        onClick={() => setSelectedCategoryPath([])}
                        className="flex items-center text-primary-600 hover:text-primary-700 text-sm"
                      >
                        <ChevronRightIcon className="h-4 w-4 mr-1" />
                        All Ads
                      </button>
                      
                      {/* Show breadcrumb path */}
                      {selectedCategoryPath.map((pathItem, index) => (
                        <div key={pathItem.id} className="flex items-center">
                          <ChevronRightIcon className="h-3 w-3 text-gray-400 mx-1" />
                          <button
                            onClick={() => setSelectedCategoryPath(selectedCategoryPath.slice(0, index + 1))}
                            className="text-sm text-gray-600 hover:text-primary-600"
                          >
                            {pathItem.name}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show current level categories */}
                  {getCurrentLevelCategories().map((category) => {
                    const childCategories = getChildCategories(category.id);
                    const hasChildren = childCategories.length > 0;
                    const isSelected = filters.categoryId === category.id;
                    const indentLevel = selectedCategoryPath.length;
                    
                    return (
                      <div
                        key={category.id}
                        onClick={() => {
                          // Always apply filter when clicking on a category (includes parent and all subcategories)
                          handleFilterChange('categoryId', category.id);
                          if (hasChildren) {
                            // Also navigate to subcategories for UI navigation
                            setSelectedCategoryPath([...selectedCategoryPath, category]);
                          }
                        }}
                        className="flex items-center p-3  cursor-pointer transition-colors hover:bg-gray-50"
                        style={{ 
                          paddingLeft: `${12 + (indentLevel * 10)}px`,
                          borderLeft: isSelected ? '1px solid #aa0001' : (indentLevel > 1 ? '1px solid #e5e7eb' : 'none'),
                          marginLeft: indentLevel > 1 ? '18px' : '0'
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          {indentLevel === 0 && category.iconUrl && (
                            <span className="text-gray-700">
                              {category.iconUrl === 'home' && <HomeIcon className="h-5 w-5" />}
                              {category.iconUrl === 'car' && <TruckIcon className="h-5 w-5" />}
                              {category.iconUrl === 'electronics' && <DevicePhoneMobileIcon className="h-5 w-5" />}
                              {category.iconUrl === 'tools' && <WrenchScrewdriverIcon className="h-5 w-5" />}
                              {!['home', 'car', 'electronics', 'tools'].includes(category.iconUrl) && <FolderIcon className="h-5 w-5" />}
                            </span>
                          )}
                          <span className={`text-sm ${isSelected ? 'text-primary-700 font-medium' : 'text-gray-700'}`}>
                            {category.name}
                          </span>
                        </div>
                      </div>
                    );
                  })}


                </div>
              </div>

              {/* Price - Divar Style */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Price</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                                          placeholder="e.g. 10,000"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                  <input
                    type="number"
                                          placeholder="e.g. 100,000"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>



              {/* Category Attributes */}
              {categoryAttributes?.data && categoryAttributes.data.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Special Features</h3>
                  <div className="space-y-3">
                    {categoryAttributes.data
                      .filter(attr => attr.isFilterable)
                      .map((attribute) => (
                        <div key={attribute.id}>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            {attribute.label}
                          </label>
                          {renderAttributeFilter(attribute)}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-xl font-semibold text-gray-900">
                    {totalCount.toLocaleString('en-US')} ads found
                  </h1>
                  {filters.searchTerm && (
                                          <p className="text-gray-600">for "{filters.searchTerm}"</p>
                  )}
                  
                  {/* Active filters display */}
                  {getActiveFiltersCount() > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {filters.categoryId && selectedCategory && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {selectedCategory.name}
                          <button
                            onClick={() => handleFilterChange('categoryId', '')}
                            className="ml-1 hover:text-primary-600"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                      {(filters.minPrice || filters.maxPrice) && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {filters.minPrice && `$${filters.minPrice}`}
                          {filters.minPrice && filters.maxPrice && ' - '}
                          {filters.maxPrice && `$${filters.maxPrice}`}
                          <button
                            onClick={() => {
                              handleFilterChange('minPrice', '');
                              handleFilterChange('maxPrice', '');
                            }}
                            className="ml-1 hover:text-primary-600"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                                     {/* Sort */}
                   <div className="relative">
                     <select
                       value={sortBy}
                       onChange={(e) => setSortBy(e.target.value)}
                       className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                       style={{ backgroundImage: 'none' }}
                     >
                       <option value="date_new">Newest</option>
                                               <option value="date_old">Oldest</option>
                        <option value="price_low">Price: Low to High</option>
                        <option value="price_high">Price: High to Low</option>
                     </select>
                     <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                   </div>

                  {/* View Toggle */}
                  <div className="flex border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600'} rounded-l-lg`}
                    >
                      <Squares2X2Icon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600'} rounded-r-lg`}
                    >
                      <ListBulletIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <DealdenLoader 
                  size="medium"
                  message="Searching for ads..."
                />
              </div>
            )}

            {/* No Results */}
            {!isLoading && ads.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                              <h3 className="text-xl font-semibold text-gray-900 mb-2">No ads found</h3>
              <p className="text-gray-600 mb-6">Try changing your search filters</p>
                <button
                  onClick={clearFilters}
                  className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* Results Grid/List */}
            {!isLoading && ads.length > 0 && (
              <>
                <div className={viewMode === 'grid' ? 
                  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8' : 
                  'space-y-4 mb-8'
                }>
                  {ads.map((ad) => (
                    <AdCard key={ad.id} ad={ad} viewMode={viewMode} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 space-x-reverse">
                    {/* Previous Button */}
                    <button
                      onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {(() => {
                      const pagesToShow = Math.min(5, totalPages);
                      const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - pagesToShow + 1));
                      const pageNumbers: number[] = [];
                      for (let i = 0; i < pagesToShow; i++) {
                        const pageNum = startPage + i;
                        if (pageNum <= totalPages && !pageNumbers.includes(pageNum)) {
                          pageNumbers.push(pageNum);
                        }
                      }
                      return pageNumbers.map((pageNumber) => (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg ${
                            currentPage === pageNumber
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ));
                    })()}

                    {/* Next Button */}
                    <button
                      onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cities Modal */}
      <CitiesModal
        isOpen={isCitiesModalOpen}
        onClose={closeCitiesModal}
        onConfirm={handleCitiesSelect}
        initialSelectedCities={selectedCities}
      />
    </div>
  );
};

// Ad Card Component
interface AdCardProps {
  ad: AdListItem;
  viewMode: 'grid' | 'list';
}

const AdCard: React.FC<AdCardProps> = ({ ad, viewMode }) => {
  const [isFavorited, setIsFavorited] = useState(ad.isFavorited);

  const handleFavoriteToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Toggle favorite logic here
    setIsFavorited(!isFavorited);
  };

  // Advanced CSS effects for different promotional types
  const getPromotionalClasses = () => {
    const effects = [];
    
    // Base promotional styling
    if (ad.isHighlighted || ad.isUrgent || ad.isTopAd || ad.isFeatured) {
      effects.push("promoted-hover");
    }
    
    // Specific effects for each promotional type
    if (ad.isHighlighted) {
      effects.push("glow-highlighted", "float-effect", "shimmer-effect");
    }
    
    if (ad.isUrgent) {
      effects.push("glow-urgent", "animate-pulse-gentle");
    }
    
    if (ad.isTopAd) {
      effects.push("glow-top-ad", "rainbow-border");
    }
    
    if (ad.isFeatured) {
      effects.push("glow-featured", "highlighted-gradient-border");
    }
    
    return effects.join(" ");
  };

  const getContentStyling = () => {
    const styles = [];
    
    if (ad.isHighlighted) {
      styles.push("bg-gradient-to-br from-orange-50 to-amber-50", "pattern-dots");
    }
    
    if (ad.isTopAd) {
      styles.push("backdrop-highlight");
    }
    
    if (ad.isFeatured) {
      styles.push("bg-gradient-to-br from-yellow-50 to-orange-50");
    }
    
    return styles.join(" ");
  };

  const getTitleStyling = () => {
    if (ad.isHighlighted) {
      return "neon-text";
    }
    return "";
  };

  if (viewMode === 'list') {
    return (
      <Link to={`/ads/${ad.slug || ad.id}`} className="block">
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300 ${getPromotionalClasses()}`}>
          <div className={`${getContentStyling()} rounded-lg ${(ad.isHighlighted || ad.isTopAd || ad.isFeatured) ? 'p-4 -m-2' : ''}`}>
            <div className="flex space-x-6">
            <div className="flex-shrink-0">
              <img
                src={(() => {
                  const imageUrl = getImageUrl(ad.primaryImageUrl);
                  console.log('SearchPage List View - Ad:', ad.id, 'Title:', ad.title, 'primaryImageUrl (raw):', ad.primaryImageUrl, 'getImageUrl result:', imageUrl);
                  return imageUrl;
                })()}
                alt={ad.title}
                className="w-32 h-24 object-cover rounded-lg"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold text-gray-900 line-clamp-1 hover:text-primary-600 ${getTitleStyling()}`}>
                    {ad.title}
                  </h3>
                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center space-x-1">
                      <MapPinIcon className="h-4 w-4" />
                      <span>{ad.city}, {ad.province}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{new Date(ad.datePosted).toLocaleDateString('en-CA')}</span>
                    </span>
                  </div>
                  <div className="mt-2 flex items-center space-x-3">
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">
                      {ad.categoryName}
                    </span>
                    {ad.isFeatured && (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                        Featured
                      </span>
                    )}
                    {ad.isUrgent && (
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                        Urgent
                      </span>
                    )}
                    {ad.isHighlighted && (
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">
                        Highlighted
                      </span>
                    )}
                    {ad.isTopAd && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                        Top
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-semibold text-green-600">
                    {new Intl.NumberFormat('en-CA', {
                      style: 'currency',
                      currency: 'CAD'
                    }).format(ad.price)}
                  </div>
                  <button
                    onClick={handleFavoriteToggle}
                    className="mt-2 p-2 text-gray-400 hover:text-red-500"
                  >
                    {isFavorited ? (
                      <HeartSolidIcon className="h-5 w-5 text-red-500" />
                    ) : (
                      <HeartIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/ads/${ad.slug || ad.id}`} className="block group">
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group-hover:border-primary-300 ${getPromotionalClasses()}`}>
        <div className={`${getContentStyling()} ${(ad.isHighlighted || ad.isTopAd || ad.isFeatured) ? 'p-2 -m-1' : ''}`}>
        <div className="relative">
          <img
            src={(() => {
              const imageUrl = getImageUrl(ad.primaryImageUrl);
              console.log('SearchPage Grid View - Ad:', ad.id, 'Title:', ad.title, 'primaryImageUrl (raw):', ad.primaryImageUrl, 'getImageUrl result:', imageUrl);
              return imageUrl;
            })()}
            alt={ad.title}
            className="w-full h-48 object-cover"
          />
          <button
            onClick={handleFavoriteToggle}
            className="absolute top-3 left-3 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
          >
            {isFavorited ? (
              <HeartSolidIcon className="h-5 w-5 text-red-500" />
            ) : (
              <HeartIcon className="h-5 w-5 text-gray-600" />
            )}
          </button>
          <div className="absolute top-3 right-3 flex flex-col gap-1">
            {ad.isUrgent && (
              <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                Urgent
              </div>
            )}
            {ad.isHighlighted && (
              <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                Featured
              </div>
            )}
            {ad.isTopAd && (
              <div className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                Top
              </div>
            )}
            {ad.isFeatured && (
              <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                Special
              </div>
            )}
          </div>
        </div>
        <div className="p-4">
          <h3 className={`font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600 mb-2 ${getTitleStyling()}`}>
            {ad.title}
          </h3>
          <div className="text-xl font-semibold text-green-600 mb-2">
            {new Intl.NumberFormat('en-CA', {
              style: 'currency',
              currency: 'CAD'
            }).format(ad.price)}
          </div>
          <div className="flex items-center text-gray-500 text-sm mb-2 space-x-1 space-x-reverse">
            <MapPinIcon className="h-4 w-4" />
            <span>{ad.city}, {ad.province}</span>
          </div>
          <div className="flex items-center justify-between text-gray-500 text-sm">
            <span>{ad.categoryName}</span>
            <span>{new Date(ad.datePosted).toLocaleDateString('en-US')}</span>
          </div>
        </div>
        </div>
      </div>
    </Link>
  );
};

export default SearchPage; 

